<template>
  <Dialog :open="showModal" @update:open="(e) => $emit('update:show-modal', e)">
    <DialogContent>
      <div v-if="isLoading" class="flex justify-center">
        <LayupSpinner />
      </div>
      <template v-else>
        <template v-if="showSuccess">
          <DialogHeader class="items-center">
            <SuccessIcon />
            <DialogTitle>We'll Be In Touch Soon!</DialogTitle>
            <DialogDescription>
              Thanks for reaching out. Our support team is reviewing your message and will get back to you within 24
              hours. Keep an eye on your inbox - we will be in touch soon!
            </DialogDescription>
          </DialogHeader>

          <SectionDivider className="my-4" />

          <DialogFooter>
            <Button @click="onClose">Done</Button>
          </DialogFooter>
        </template>
        <template v-else>
          <DialogHeader>
            <DialogTitle>Submit a Support Ticket</DialogTitle>
            <DialogDescription>
              Think it's a bug? If it's not in the system status or a known issue, report it here and we'll escalate it
              to our developers.
            </DialogDescription>
            <span v-if="error !== ''" class="text-sm font-medium text-error-500">{{ error }}</span>
          </DialogHeader>
          <div class="py-4">
            <div class="flex flex-col gap-4">
              <!-- Empty divs are to make sure errors aren't effected by gap-4. Don't remove -->
              <div>
                <TextInput
                  id="supportTicketName"
                  label="Name"
                  placeholder="John Doe"
                  test-id="support_ticket_form_name"
                  :disabled="isLoading"
                  v-model:input="formValues.name"
                />
              </div>
              <div>
                <TextInput
                  id="supportTicketEmail"
                  label="Email"
                  placeholder="john.doe@example.com"
                  :regex="emailRegex"
                  test-id="support_ticket_form_email"
                  :disabled="isLoading"
                  v-model:input="formValues.email"
                />
              </div>
              <div>
                <TextInput
                  id="supportTicketSubject"
                  label="Subject"
                  test-id="support_ticket_form_subject"
                  :disabled="isLoading"
                  v-model:input="formValues.subject"
                />
              </div>
              <div>
                <TextBoxInput
                  label="Message"
                  v-model="formValues.message"
                  :hide-label="false"
                  test-id="support_ticket_form_message"
                />
              </div>
              <div class="space-y-2">
                <div>
                  <LabelComponent>Supporting Documents</LabelComponent>
                </div>
                <div>
                  <input
                    ref="fileInput"
                    type="file"
                    multiple
                    class="hidden"
                    @change="onFileChosen"
                    accept="image/*,.pdf,.doc,.docx,.txt"
                  />
                  <Button variant="outline" class="w-auto" @click="triggerUpload">
                    <Paperclip class="h-4 w-4" />
                    Upload Files
                  </Button>
                  <div class="text-xs text-grey-light-400">Upload up to 3 files (1MB max each)</div>
                </div>
                <div v-if="uploadingFiles" class="flex justify-center mt-2">
                  <LayupSpinner />
                </div>
                <div v-if="attachments.length > 0 && !uploadingFiles" class="mt-2">
                  <ul class="flex flex-col gap-2 file-list">
                    <li
                      v-for="(file, index) in attachments"
                      :key="file.name"
                      class="flex justify-between items-center text-sm text-grey-light-700 px-4 py-2 border border-grey-light-200 rounded-lg"
                    >
                      <div class="flex flex-col">
                        <span class="font-semibold text-grey-light-800 truncate max-w-[350px]">
                          {{ file.name }}
                        </span>
                        <span class="text-xs text-grey-light-500">({{ (file.size / 1024).toFixed(2) }} KB)</span>
                      </div>
                      <Button variant="ghost" size="sm" type="button" @click="removeFile(index)">
                        <X />
                      </Button>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" class="w-1/2" @click="onClose"> Cancel </Button>
            <Button
              type="button"
              class="w-1/2"
              data-testid="support_ticket_form_submit"
              :disabled="!fieldsValid"
              @click="onSubmit"
            >
              Send Message
            </Button>
          </DialogFooter>
        </template>
      </template>
    </DialogContent>
  </Dialog>
</template>
<script setup lang="ts">
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { CLOUDINARY_NAME, CLOUDINARY_UPLOAD_PRESET } from '@/config';
import { NetworkError } from '@/errors';
import { sendEmail } from '@/services/emails';
import { useMerchantStore } from '@/stores/merchants';
import { useUserStore } from '@/stores/users';
import { addMetric, createException, createWarning } from '@/utils/monitoring.utils';
import axios from 'axios';
import { Paperclip, X } from 'lucide-vue-next';
import { computed, reactive, ref, watch } from 'vue';
import LayupSpinner from '../common/LayupSpinner.vue';
import SectionDivider from '../common/SectionDivider.vue';
import SuccessIcon from '../icons/SuccessIcon.vue';
import { Button } from '../ui/button';
import LabelComponent from '../ui/label/LabelComponent.vue';
import TextBoxInput from './TextBoxInput.vue';
import TextInput from './TextInput.vue';

const props = defineProps<{ showModal: boolean }>();

const emit = defineEmits(['update:show-modal']);

const userStore = useUserStore();
const merchantStore = useMerchantStore();
const { user } = userStore;

const fileInput = ref<HTMLInputElement | null>(null);
const isLoading = ref(false);
const uploadingFiles = ref(false);
const error = ref('');
const showSuccess = ref(false);
const attachments = ref<File[]>([]);

const fieldsValid = computed(() => {
  return (
    formValues.name.trim() !== '' &&
    emailRegex.test(formValues.email.trim()) &&
    formValues.subject.trim() !== '' &&
    formValues.message.trim() !== ''
  );
});

const emailRegex = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/;

const initialFormValues = {
  name: '',
  email: '',
  subject: '',
  message: '',
  merchantName: '',
  merchantId: '',
  attachment: [] as string[],
  type: 'support',
};

const formValues = reactive({ ...initialFormValues });

watch(
  () => props.showModal,
  async (newValue) => {
    if (newValue) {
      formValues.name = user.name ?? '';
      formValues.email = user.email ?? '';
      formValues.merchantName = merchantStore.merchant.name ?? '';
      formValues.merchantId = merchantStore.merchant._id ?? '';
    }
  },
  { immediate: true },
);

const resetForm = () => {
  Object.assign(formValues, initialFormValues);
};

const onClose = () => {
  showSuccess.value = false;
  attachments.value = [];
  emit('update:show-modal', false);
  resetForm();
};

const uploadFile = async (file: File) => {
  const cloudName = CLOUDINARY_NAME;
  const uploadPreset = CLOUDINARY_UPLOAD_PRESET;
  const apiEndpoint = `https://api.cloudinary.com/v1_1/${cloudName}/upload`;

  const formData = new FormData();
  formData.append('upload_preset', uploadPreset);
  formData.append('file', file);

  try {
    const response = await axios.post(apiEndpoint, formData, {
      headers: { 'X-Requested-With': 'XMLHttpRequest' },
      withCredentials: false,
    });

    return response.data.secure_url;
  } catch (err) {
    if (err instanceof NetworkError) {
      createWarning(err);
      addMetric('support_ticket_modal_file_upload', {
        status: 'network_error',
        channel: 'merchant',
        method: 'uploadFile',
      });
    } else {
      createException(err);
      addMetric('support_ticket_modal_file_upload', { status: 'error', channel: 'merchant', method: 'uploadFile' });
    }
  }
};

const onFileChosen = async (event: Event) => {
  error.value = '';
  uploadingFiles.value = true;
  formValues.attachment = [];

  const input = event.target as HTMLInputElement;
  if (!input.files) return;

  const files = Array.from(input.files);

  if (files.length > 3) {
    error.value = 'You can only upload up to 3 files.';
    return;
  }

  const validFiles = files.filter((file) => file.size <= 1024 * 1024); // 1MB max
  const tooLarge = files.filter((file) => file.size > 1024 * 1024);

  if (tooLarge.length) {
    error.value = `Some files exceed 1MB: ${tooLarge.map((f) => f.name).join(', ')}`;
  }

  const uploadResults: string[] = await Promise.all(validFiles.map((file) => uploadFile(file)));

  if (uploadResults.some((url) => !url)) {
    error.value = 'Failed to upload one or more files. Please try again.';
    uploadingFiles.value = false;
    return;
  }

  // For displaying file names to the user in the UI
  attachments.value = validFiles;

  // The URL's of the uploaded files that will be attached to the email
  formValues.attachment = uploadResults;

  uploadingFiles.value = false;
};

const removeFile = (index: number) => {
  attachments.value.splice(index, 1);
  formValues.attachment.splice(index, 1);
};

const triggerUpload = () => {
  fileInput.value?.click();
};

const onSubmit = async () => {
  error.value = '';

  if (!fieldsValid.value) {
    error.value = 'Please fill in all fields before submitting.';
    return;
  }

  try {
    isLoading.value = true;
    await sendEmail({ ...formValues });
    showSuccess.value = true;
  } catch (error) {
    if (error instanceof NetworkError) {
      createWarning(error);
      addMetric(`support_ticket_modal`, { status: 'network_error', channel: 'merchant' });
    } else {
      createException(error);
      addMetric(`support_ticket_modal`, { status: 'error', channel: 'merchant' });
    }
  } finally {
    isLoading.value = false;
  }
};
</script>
